<template>
  <div>
    <h2 class="reliance-dashboard__print-section-title">
      Cost Module
    </h2>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Loading..." />
      <p class="mt-2 text-muted">Loading cost module data...</p>
    </div>

    <div v-else-if="loadError" class="py-3">
      <b-alert show variant="danger">
        {{ loadError }}
      </b-alert>
    </div>

    <div v-else>
      <!-- Row 1: Top KPI cards (colored backgrounds, no icons) -->
      <b-row class="match-height mb-2">
        <b-col
          v-for="kpi in topKpiCards"
          :key="kpi.key"
          cols="12"
          sm="6"
          md="4"
          lg
          xl="2"
          class="mb-1"
        >
          <b-card
            no-body
            class="mb-0"
            :class="`bg-light-${kpi.color}`"
          >
            <b-card-body class="p-1">
              <small class="d-block font-weight-bold">{{ kpi.label }}</small>
              <h4 class="font-weight-bolder mb-0">{{ kpi.displayValue }}</h4>
              <div
                v-if="kpi.trend"
                class="d-flex align-items-center mt-50"
              >
                <feather-icon
                  :icon="kpi.trend.value >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'"
                  size="12"
                  :class="kpi.trend.value >= 0 ? 'text-success' : 'text-danger'"
                />
                <small
                  class="ml-25"
                  :class="kpi.trend.value >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ Math.abs(kpi.trend.value) }}%
                </small>
                <small class="text-muted ml-50">{{ kpi.trend.label }}</small>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Row 2: Cost by Department -->
      <b-card no-body class="mb-2">
        <b-card-header class="pb-0">
          <h5 class="mb-0 font-weight-bold">Cost by Department</h5>
        </b-card-header>
        <b-card-body class="pt-1">
          <b-row>
            <b-col
              v-for="section in costTypeSections"
              :key="`dept-${section.key}`"
              cols="12"
              md="4"
              class="mb-2 mb-md-0"
            >
              <b-card no-body class="mb-0 border">
                <b-card-header
                  class="py-1"
                  :class="`bg-${section.color}`"
                >
                  <h6 class="mb-0 text-white font-weight-bold">{{ section.title }}</h6>
                </b-card-header>
                <b-card-body class="p-1">
                  <b-list-group
                    v-if="metricRowsForSummary(departmentSummary(section.key)).length"
                    flush
                  >
                    <b-list-group-item
                      v-for="row in metricRowsForSummary(departmentSummary(section.key))"
                      :key="`${section.key}-${row.metric}`"
                      class="d-flex justify-content-between align-items-center py-1 border-0"
                    >
                      <span>{{ row.metric }}</span>
                      <b-badge
                        pill
                        :variant="`light-${row.badgeColor}`"
                      >
                        {{ formatMetricValue(row) }}
                      </b-badge>
                    </b-list-group-item>
                  </b-list-group>
                  <p
                    v-else
                    class="text-muted small mb-0 text-center py-2"
                  >
                    No data available
                  </p>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Row 3: Chart + Cost by Function -->
      <b-row>
        <b-col cols="12" lg="6" class="mb-2">
          <b-card no-body class="mb-0">
            <b-card-header class="d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h5 class="mb-0">Cost trend over time</h5>
              <b-form-select
                v-model="trendPeriod"
                :options="trendPeriodOptions"
                size="sm"
                class="mt-50 mt-lg-0"
              />
            </b-card-header>
            <b-card-body class="px-1 pb-1 pt-50">
              <vue-apex-charts
                v-if="costTrendSeries[0].data.length"
                type="line"
                height="280"
                :options="costTrendOptions"
                :series="costTrendSeries"
              />
              <div
                v-else
                class="d-flex align-items-center justify-content-center text-muted"
                style="height:280px"
              >
                No trend data for the selected period
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <b-col cols="12" lg="6" class="mb-2">
          <b-card no-body class="mb-0 border">
            <b-card-header class="bg-info py-1">
              <h5 class="mb-0 text-white font-weight-bold">Cost by Function</h5>
            </b-card-header>
            <b-card-body class="p-1">
              <b-list-group
                v-if="costData.byFunction && costData.byFunction.length"
                flush
              >
                <b-list-group-item
                  v-for="(fn, idx) in costData.byFunction"
                  :key="fn.functionName"
                  class="d-flex justify-content-between align-items-center py-1 border-0"
                >
                  <span class="font-weight-bold">{{ fn.functionName }}</span>
                  <b-badge
                    pill
                    :variant="`light-${functionBadgeColor(idx)}`"
                  >
                    {{ formatCurrency(fn.totalCost) }}
                  </b-badge>
                </b-list-group-item>
              </b-list-group>
              <p
                v-else
                class="text-muted small mb-0 text-center py-2"
              >
                No function data
              </p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Row 4: Cost by Role -->
      <b-card no-body class="mb-2">
        <b-card-header class="pb-0">
          <h5 class="mb-0 font-weight-bold">Cost by Role</h5>
        </b-card-header>
        <b-card-body class="pt-1">
          <b-row>
            <b-col
              v-for="section in costTypeSections"
              :key="`role-${section.key}`"
              cols="12"
              md="4"
              class="mb-2 mb-md-0"
            >
              <b-card no-body class="mb-0 border">
                <b-card-header
                  class="py-1"
                  :class="`bg-${section.color}`"
                >
                  <h6 class="mb-0 text-white font-weight-bold">{{ section.title }}</h6>
                </b-card-header>
                <b-card-body class="p-1">
                  <b-list-group
                    v-if="metricRowsForSummary(roleSummary(section.key)).length"
                    flush
                  >
                    <b-list-group-item
                      v-for="row in metricRowsForSummary(roleSummary(section.key))"
                      :key="`${section.key}-${row.metric}`"
                      class="d-flex justify-content-between align-items-center py-1 border-0"
                    >
                      <span>{{ row.metric }}</span>
                      <b-badge
                        pill
                        :variant="`light-${row.badgeColor}`"
                      >
                        {{ formatMetricValue(row) }}
                      </b-badge>
                    </b-list-group-item>
                  </b-list-group>
                  <p
                    v-else
                    class="text-muted small mb-0 text-center py-2"
                  >
                    No data available
                  </p>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Row 5: Bottom KPI cards -->
      <b-row class="match-height mb-2">
        <b-col
          v-for="kpi in bottomKpiCards"
          :key="kpi.key"
          cols="12"
          md="6"
          class="mb-1"
        >
          <statistic-card-horizontal
            :statistic="kpi.displayValue"
            :statistic-title="kpi.label"
            :icon="kpi.icon"
            :color="kpi.color"
          />
          <div
            v-if="kpi.trend"
            class="d-flex align-items-center mt-50 px-1"
          >
            <feather-icon
              :icon="kpi.trend.value >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'"
              size="12"
              :class="kpi.trend.value >= 0 ? 'text-success' : 'text-danger'"
            />
            <small
              class="ml-25"
              :class="kpi.trend.value >= 0 ? 'text-success' : 'text-danger'"
            >
              {{ Math.abs(kpi.trend.value) }}%
            </small>
            <small class="text-muted ml-50">{{ kpi.trend.label }}</small>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BBadge,
  BSpinner,
  BAlert,
  BFormSelect,
} from 'bootstrap-vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import RelianceService from '@/services/reliance-service'

const COST_TYPE_SECTIONS = [
  { key: 'lateral', title: 'Lateral', color: 'primary' },
  { key: 'fteOms', title: 'FTE-O&Ms', color: 'success' },
  { key: 'fteOsUs', title: 'FTE-Os&Us', color: 'warning' },
]

const METRIC_BADGE_COLORS = ['info', 'primary', 'success', 'warning', 'secondary']

const FUNCTION_BADGE_COLORS = ['primary', 'success', 'info', 'warning', 'secondary', 'danger']

const EMPTY_COST_DATA = {
  summary: {},
  costTrend: { daily: { labels: [], values: [] }, weekly: { labels: [], values: [] }, monthly: { labels: [], values: [] } },
  byDepartment: { lateral: null, fteOms: null, fteOsUs: null },
  byRole: { lateral: null, fteOms: null, fteOsUs: null },
  byFunction: [],
}

export default {
  name: 'CostModuleTab',
  components: {
    VueApexCharts,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BBadge,
    BSpinner,
    BAlert,
    BFormSelect,
    StatisticCardHorizontal,
  },
  props: {
    clientName: {
      type: String,
      default: '',
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      costDataLoading: false,
      loadError: null,
      costData: { ...EMPTY_COST_DATA },
      trendPeriod: 'daily',
      trendPeriodOptions: [
        { value: 'daily', text: 'Daily' },
        { value: 'weekly', text: 'Weekly' },
        { value: 'monthly', text: 'Monthly' },
      ],
      costTypeSections: COST_TYPE_SECTIONS,
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.costDataLoading
    },
    summary() {
      return this.costData.summary || {}
    },
    trends() {
      return this.summary.trends || {}
    },
    topKpiCards() {
      const s = this.summary
      const t = this.trends
      return [
        {
          key: 'totalCost',
          label: 'Total Cost',
          displayValue: this.formatCurrency(s.totalCost),
          color: 'primary',
          trend: t.totalCost,
        },
        {
          key: 'costPerHire',
          label: 'Cost per Hire',
          displayValue: this.formatCurrency(s.costPerHire),
          color: 'info',
          trend: t.costPerHire,
        },
        {
          key: 'totalHiringCost',
          label: 'Total Hiring Cost',
          displayValue: this.formatCurrency(s.totalHiringCost),
          color: 'success',
          trend: t.totalHiringCost,
        },
        {
          key: 'totalOnboarding',
          label: 'Total Onboarding',
          displayValue: this.formatCurrency(s.totalOnboarding),
          color: 'warning',
          trend: t.totalOnboarding,
        },
        {
          key: 'totalHires',
          label: 'Total Hires',
          displayValue: this.formatCount(s.totalHires),
          color: 'secondary',
          trend: t.totalHires,
        },
      ]
    },
    bottomKpiCards() {
      const s = this.summary
      const t = this.trends
      return [
        {
          key: 'offeredPipeline',
          label: 'Offered Pipeline',
          displayValue: this.formatCount(s.offeredPipeline),
          icon: 'AwardIcon',
          color: 'primary',
          trend: t.offeredPipeline,
        },
        {
          key: 'upcomingCost',
          label: 'Upcoming Cost',
          displayValue: this.formatCurrency(s.upcomingCost),
          icon: 'CalendarIcon',
          color: 'danger',
          trend: t.upcomingCost,
        },
      ]
    },
    activeTrendData() {
      const trend = this.costData.costTrend || {}
      return trend[this.trendPeriod] || { labels: [], values: [] }
    },
    costTrendSeries() {
      const { labels, values } = this.activeTrendData
      return [{
        name: 'Total Cost',
        data: (values || []).map((y, i) => ({
          x: (labels || [])[i] || String(i),
          y,
        })),
      }]
    },
    costTrendOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: { show: false },
          fontFamily: 'inherit',
          zoom: { enabled: false },
        },
        stroke: { curve: 'smooth', width: 3 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        colors: ['#7367f0'],
        markers: { size: 4 },
        dataLabels: { enabled: false },
        xaxis: {
          labels: { style: { fontSize: '10px' } },
        },
        yaxis: {
          labels: {
            style: { fontSize: '10px' },
            formatter: v => this.formatCompactCurrency(v),
          },
        },
        grid: { borderColor: '#ebe9f1' },
        tooltip: {
          y: {
            formatter: v => this.formatCurrency(v),
          },
        },
      }
    },
  },
  watch: {
    clientName() {
      this.loadCostData()
    },
    filters: {
      deep: true,
      handler() {
        this.loadCostData()
      },
    },
    loading(val, oldVal) {
      if (oldVal && !val) {
        this.loadCostData()
      }
    },
  },
  mounted() {
    this.loadCostData()
  },
  methods: {
    departmentSummary(key) {
      const dept = this.costData.byDepartment || {}
      return dept[key] || null
    },
    roleSummary(key) {
      const role = this.costData.byRole || {}
      return role[key] || null
    },
    functionBadgeColor(index) {
      return FUNCTION_BADGE_COLORS[index % FUNCTION_BADGE_COLORS.length]
    },
    metricRowsForSummary(summary) {
      if (!summary) return []
      const rows = [
        { metric: 'Total Hires', value: summary.totalHire, format: 'count' },
        { metric: 'Total Cost', value: summary.totalCost, format: 'currency' },
        { metric: 'Cost Per Hire', value: summary.costPerHire, format: 'currency' },
        { metric: 'Total Cost per Function', value: summary.totalCostPerFunction, format: 'currency' },
        { metric: 'Average Cost Per Function', value: summary.avgCostPerFunction, format: 'currency' },
      ]
      return rows.map((row, idx) => ({
        ...row,
        badgeColor: METRIC_BADGE_COLORS[idx % METRIC_BADGE_COLORS.length],
      }))
    },
    formatMetricValue(row) {
      if (!row) return '—'
      if (row.format === 'currency') return this.formatCurrency(row.value)
      if (row.format === 'count') return this.formatCount(row.value)
      return row.value != null ? String(row.value) : '—'
    },
    formatCurrency(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(n)
    },
    formatCompactCurrency(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return ''
      if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`
      if (n >= 100000) return `${(n / 100000).toFixed(1)}L`
      if (n >= 1000) return `${(n / 1000).toFixed(0)}K`
      return String(n)
    },
    formatCount(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return new Intl.NumberFormat('en-IN').format(n)
    },
    buildApiFilters() {
      const f = this.filters || {}
      return {
        positionType: f.positionType || '',
        function: f.functionName || '',
        driveName: f.driveName || '',
        positionSubmissionStartDate: f.positionSubmissionStartDate || '',
        positionSubmissionEndDate: f.positionSubmissionEndDate || '',
      }
    },
    async loadCostData() {
      this.costDataLoading = true
      this.loadError = null
      try {
        const response = await RelianceService.getCostDashboardData(
          this.clientName,
          this.buildApiFilters(),
        )
        if (response.status === 'SUCCESS' && response.data) {
          this.costData = response.data
        } else {
          this.costData = { ...EMPTY_COST_DATA }
          this.loadError = response.message || 'Failed to load cost module data.'
        }
      } catch (err) {
        this.costData = { ...EMPTY_COST_DATA }
        this.loadError = err.message || 'Failed to load cost module data.'
      } finally {
        this.costDataLoading = false
      }
    },
  },
}
</script>
